import type { Invoice, InvoiceInsert, InvoiceUpdate } from '@/types/invoices';
import { normalizeAgreementTerms } from '@/types/agreement-terms';

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

// Mock invoices data
const mockInvoices: Invoice[] = [];

export const mockInvoicesService = {
  async getAll(): Promise<Invoice[]> {
    await delay(300);
    return [...mockInvoices];
  },

  async getById(id: string): Promise<Invoice | null> {
    await delay(200);
    return mockInvoices.find((invoice) => invoice.id === id) ?? null;
  },

  async getByDealId(dealId: string): Promise<Invoice[]> {
    await delay(200);
    return mockInvoices.filter((invoice) => invoice.deal_id === dealId);
  },

  async getByLocationOrderId(locationOrderId: string): Promise<Invoice[]> {
    await delay(200);
    return mockInvoices.filter((invoice) => invoice.location_order_id === locationOrderId);
  },

  async getByQuoteId(quoteId: string): Promise<Invoice[]> {
    await delay(200);
    return mockInvoices.filter((invoice) => invoice.quote_id === quoteId);
  },

  async create(data: InvoiceInsert): Promise<Invoice> {
    await delay(400);
    const newInvoice: Invoice = {
      id: `invoice-${Date.now()}`,
      location_id: null,
      deal_id: null,
      quote_id: null,
      location_order_id: null,
      status: 'draft',
      provisions_new_location: false,
      due_date: null,
      subtotal: 0,
      tax: 0,
      total: 0,
      notes: null,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      created_by: null,
      ...data,
      agreement_terms: normalizeAgreementTerms(data.agreement_terms),
    };
    mockInvoices.push(newInvoice);
    const { syncMockInvoicePaidWork } = await import('./mock-invoice-paid-work');
    await syncMockInvoicePaidWork(newInvoice);
    return newInvoice;
  },

  async update(id: string, data: InvoiceUpdate): Promise<Invoice> {
    await delay(400);
    const index = mockInvoices.findIndex((invoice) => invoice.id === id);
    if (index === -1) {
      throw new Error('Invoice not found');
    }
    const existing = mockInvoices[index];
    if (!existing) {
      throw new Error('Invoice not found');
    }
    const updated: Invoice = {
      ...existing,
      ...data,
      agreement_terms:
        data.agreement_terms !== undefined ? normalizeAgreementTerms(data.agreement_terms) : existing.agreement_terms,
      updated_at: new Date().toISOString(),
    };
    mockInvoices[index] = updated;
    const { syncMockInvoicePaidWork } = await import('./mock-invoice-paid-work');
    await syncMockInvoicePaidWork(updated, existing);
    return updated;
  },

  async setStatus(id: string, status: Invoice['status']): Promise<Invoice> {
    await delay(300);
    const index = mockInvoices.findIndex((invoice) => invoice.id === id);
    if (index === -1) {
      throw new Error('Invoice not found');
    }
    const existing = mockInvoices[index];
    if (!existing) {
      throw new Error('Invoice not found');
    }
    const updated: Invoice = {
      ...existing,
      status,
      updated_at: new Date().toISOString(),
    };
    mockInvoices[index] = updated;
    const { syncMockInvoicePaidWork } = await import('./mock-invoice-paid-work');
    await syncMockInvoicePaidWork(updated, existing);
    return updated;
  },

  async duplicate(sourceInvoiceId: string): Promise<Invoice> {
    await delay(300);
    const source = await this.getById(sourceInvoiceId);
    if (!source) {
      throw new Error('Invoice not found');
    }

    const { suggestDuplicateDocumentNumber } = await import('@/lib/utils/sales-document-numbers');
    const invoiceNumber = suggestDuplicateDocumentNumber(
      source.invoice_number,
      mockInvoices.map((invoice) => invoice.invoice_number)
    );

    return this.create({
      location_id: null,
      deal_id: null,
      quote_id: null,
      location_order_id: null,
      invoice_number: invoiceNumber,
      customer_name: '',
      status: 'draft',
      due_date: source.due_date,
      line_items: source.line_items.map((item) => ({ ...item })),
      subtotal: source.subtotal,
      tax: source.tax,
      total: source.total,
      notes: source.notes ? `${source.notes}\n\nDuplicated from ${source.invoice_number}.` : `Duplicated from ${source.invoice_number}.`,
      agreement_terms: source.agreement_terms,
    });
  },

  async delete(id: string): Promise<void> {
    await delay(300);
    const invoice = mockInvoices.find((item) => item.id === id) ?? null;
    const index = mockInvoices.findIndex((invoice) => invoice.id === id);
    if (index !== -1) {
      mockInvoices.splice(index, 1);
    }

    if (invoice?.quote_id) {
      const remainingInvoices = mockInvoices.filter((item) => item.quote_id === invoice.quote_id);
      if (remainingInvoices.length === 0) {
        const { mockQuotesService } = await import('./mock-quotes');
        const linkedQuote = await mockQuotesService.getById(invoice.quote_id);
        if (linkedQuote?.status === 'converted') {
          await mockQuotesService.setStatus(invoice.quote_id, 'accepted');
        }
      }
    }
  },
};
